import { Fragment, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Card, CardMedia, Container, Typography } from '@mui/material'
import Swal from 'sweetalert2'
import { useAuth } from '../../context/Auth/AuthContext'
import { useFilter } from '../../context/FilterContext/FilterContext'
import { useProducts } from '../../context/Products/ProductsContext'
import { api } from '../../services/api'
import ItemsList from '../../components/ItemsList'
import SubCategories from '../../components/SubCategories'
import FilterWidget from '../../components/FilterWidget'
import { normalizeProducts } from '../../utils/auxFunc'

const ItemsListContainer = () => {
  const [productsFiltered, setProductsFiltered] = useState(null)
  const [productsFromAPI, setProductsFromAPI] = useState(null)
  const [openFilter, setOpenFilter] = useState(false)
  const [openSort, setOpenSort] = useState(false)
  const [sortValue, setSortValue] = useState()
  const { categoryId, productsSearched, subcategoryId } = useParams()
  const { categories, dolarValue } = useProducts()
  const { auth, userData } = useAuth()
  const { filterAPI, setFilterAPI } = useFilter()
  const { clientPriceListId } = userData

  const SORT_OPTIONS = {
    relevance: (evt) => {
      setSortValue(evt.target.value)
      setProductsFiltered(productsFromAPI)
    },
    higherPrice: (evt) => {
      const dupliProducts = JSON.parse(JSON.stringify(productsFiltered))
      dupliProducts.sort((a, b) => b.price - a.price)
      setSortValue(evt.target.value)
      setProductsFiltered(dupliProducts)
    },
    lowerPrice: (evt) => {
      const dupliProducts = JSON.parse(JSON.stringify(productsFiltered))
      dupliProducts.sort((a, b) => a.price - b.price)
      setSortValue(evt.target.value)
      setProductsFiltered(dupliProducts)
    }
  }

  const DEFAULT_SORT = () => {
    setSortValue()
    setProductsFiltered(productsFromAPI)
  }

  const toggleFilterMenu = () => {
    setOpenFilter((prevState) => !prevState)
  }

  const toggleSortMenu = () => {
    setOpenSort((prevState) => !prevState)
  }

  const handleSort = (evt) =>
    SORT_OPTIONS[evt.target.value]
      ? SORT_OPTIONS[evt.target.value](evt)
      : DEFAULT_SORT()

  // ----- Integración con API -----

  const getByCategory = (url, signal) => {
    api.products
      .listByCategory(url, signal)
      .then((response) => response.json())
      .then((productsByCategory) => {
        const normalizedProducts = Array.isArray(productsByCategory)
          ? normalizeProducts(productsByCategory)
          : []

        setProductsFromAPI(normalizedProducts)
        setProductsFiltered(normalizedProducts)
      })
      .catch((err) => {
        if (err.name === 'AbortError') return
        return Swal.fire({
          title: 'Error!',
          text: `Error de servidor: ${err}`,
          icon: 'error'
        })
      })
  }

  const getProductsSearched = (url, signal) => {
    api.products
      .listProductsSearched(url, signal)
      .then((response) => response.json())
      .then((productsSearched) => {
        const normalizedProducts = Array.isArray(productsSearched)
          ? normalizeProducts(productsSearched)
          : []
        setProductsFromAPI(normalizedProducts)
        setProductsFiltered(normalizedProducts)
      })
      .catch((err) => {
        if (err.name === 'AbortError') return
        return Swal.fire({
          title: 'Error!',
          text: `Error de servidor: ${err.message}`,
          icon: 'error'
        })
      })
  }

  const subCategories = categories?.find(
    (category) =>
      category.name.toLowerCase() === categoryId?.replaceAll('_', ' ')
  )?.subcategories

  const bannerCategory =
    categoryId &&
    categories?.find(
      (category) =>
        category.name.toLowerCase() ===
        categoryId?.replaceAll('_', ' ').toLowerCase()
    )?.bannerURL

  useEffect(() => {
    const controller = new AbortController()
    if (categoryId) {
      const urlCategory = `${categoryId}/${subcategoryId || '%20'}/${
        filterAPI?.color?.color || 0
      }/${filterAPI?.sizeLetter?.sizeLetter || '%20'}/${
        filterAPI?.price[0] / dolarValue || 0
      }/${
        filterAPI?.price[1] / dolarValue || 99999999
      }?codigoLista=${clientPriceListId}`

      getByCategory(urlCategory, controller.signal)
    } else {
      const urlSearch = `${productsSearched}/${categoryId || '%20'}/${
        subcategoryId || '%20'
      }/${filterAPI?.color?.color || '%20'}/${
        filterAPI?.sizeLetter?.sizeLetter || '%20'
      }/${filterAPI?.price[0] / dolarValue || 0}/${
        filterAPI?.price[1] / dolarValue || 99999999
      }?codigoLista=${clientPriceListId}`

      getProductsSearched(urlSearch, controller.signal)
    }

    return () => {
      controller.abort()
    }
  }, [
    categoryId,
    clientPriceListId,
    filterAPI?.color,
    filterAPI?.price,
    filterAPI?.sizeLetter,
    productsSearched,
    setFilterAPI,
    subcategoryId,
    dolarValue
  ])

  return (
    <Fragment>
      {!!categoryId && (
        <Card
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            borderRadius: 0
          }}
        >
          <CardMedia
            component="img"
            alt="Imagen de categorías"
            image={bannerCategory}
            sx={{
              maxHeight: '500px',
              width: 'auto',
              maxWidth: '100%',
              objectFit: 'contain'
            }}
          ></CardMedia>
        </Card>
      )}
      <Container maxWidth="xl">
        <Box>
          <Typography
            variant="h1"
            sx={{
              textAlign: 'center',
              fontFamily: 'Quicksand',
              fontSize: '2rem',
              fontWeight: 'bold',
              textTransform: 'capitalize',
              pt: 2
            }}
          >
            {categoryId?.replaceAll('_', ' ').toLowerCase()}
          </Typography>
        </Box>
        {subCategories && <SubCategories subCategories={subCategories} />}
        <FilterWidget
          toggleFilterMenu={toggleFilterMenu}
          showFilter={!!openFilter}
          toggleSortMenu={toggleSortMenu}
          handleSort={handleSort}
          showSort={!!openSort}
          sortValue={sortValue}
        />
        <ItemsList
          showPrice={!!auth}
          products={productsFiltered}
          showCategoryTexts={!!categoryId}
        />
      </Container>
    </Fragment>
  )
}

export default ItemsListContainer
